import { Injectable, inject, signal, computed } from '@angular/core';
import { OrganizationService } from '../../features/settings/services/organization.service';
import { AuthService } from '../../auth/services/auth.service';
import { IOrganizationConfig } from '../../../types/IOrganizationConfig';
import { extractErrorMessage } from '../../shared/utils/http-error';
import { NotificationService } from './notification.service';

/**
 * Application-wide organization branding and defaults.
 *
 * Root-provided, so it lives for the whole session — there is no teardown hook
 * to hang cleanup off. The logo object URL is instead revoked whenever it is
 * replaced, which is the only moment one can leak.
 */
@Injectable({ providedIn: 'root' })
export class OrganizationStore {
  private readonly organizationService = inject(OrganizationService);
  private readonly auth = inject(AuthService);
  private readonly notifications = inject(NotificationService);

  private readonly _title = signal<string>('Organizae');
  private readonly _logoUrl = signal<string | null>(null);
  private readonly _defaultGainPercentage = signal<number | null>(null);
  private readonly _defaultProductStatusId = signal<string | null>(null);
  private readonly _defaultUnitOfMeasureId = signal<string | null>(null);

  /**
   * Whether the configuration fetch has settled, successfully or not. Consumers need this to tell
   * "still waiting" from "loaded, and there is no default" — a distinction a null value cannot make,
   * and one the product form depends on to avoid blocking submit forever after a failed load.
   */
  private readonly _configLoaded = signal(false);

  readonly displayTitle = computed(() => this._title());
  readonly displayLogoUrl = computed(() => this._logoUrl());
  readonly defaultGainPercentage = computed(() => this._defaultGainPercentage());
  readonly defaultProductStatusId = computed(() => this._defaultProductStatusId());
  readonly defaultUnitOfMeasureId = computed(() => this._defaultUnitOfMeasureId());
  readonly configLoaded = computed(() => this._configLoaded());

  loadConfig(): void {
    if (!this.auth.isAuthenticated()) return;

    this.organizationService.getConfig().subscribe({
      next: (config) => this.applyConfig(config),
      // A configuration outage must not block product registration, so the fetch is marked settled
      // either way and consumers fall back to "no defaults configured".
      error: (err) => {
        this._configLoaded.set(true);
        this.notifications.error(extractErrorMessage(err, 'Erro ao carregar as configurações da organização'));
      }
    });
  }

  refreshAfterUpdate(config: IOrganizationConfig): void {
    this.applyConfig(config);
  }

  private applyConfig(config: IOrganizationConfig): void {
    this._title.set(config.title);
    this._defaultGainPercentage.set(config.estimatedPercentageOfGainPerProduct);
    this._defaultProductStatusId.set(config.defaultProductStatusId ?? null);
    this._defaultUnitOfMeasureId.set(config.defaultUnitOfMeasureId ?? null);
    this._configLoaded.set(true);

    if (config.hasLogo) {
      this.loadLogo();
    } else {
      this.revokeLogoUrl();
    }
  }

  private loadLogo(): void {
    this.organizationService.getLogoBlob().subscribe({
      next: (blob) => {
        this.revokeLogoUrl();
        this._logoUrl.set(URL.createObjectURL(blob));
      }
    });
  }

  private revokeLogoUrl(): void {
    const current = this._logoUrl();
    if (current) {
      URL.revokeObjectURL(current);
      this._logoUrl.set(null);
    }
  }
}
